import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { ApiRequestError } from '@/lib/api';

import { deletePushDevice, fetchPushDevices, fetchPushVapidPublicKey } from './push-api';
import { clearPushPromptDeclinedOnDevice } from './push-prompt-preferences';
import {
  readCurrentPushEndpoint,
  subscribeAndSyncPushSubscription,
  unregisterCurrentPushDevice,
} from './register-push';
import { buildUnsupportedPushSubscriptionSnapshot } from './push-unsupported';
import {
  isPushSupported,
  readNotificationPermission,
  type PushPermissionState,
} from './push-support';

export { createPushUnsupportedError } from './push-unsupported';

export const pushVapidQueryKey = ['notifications', 'push', 'vapid'] as const;
export const pushDevicesQueryKey = ['notifications', 'push', 'devices'] as const;
export const pushCurrentEndpointQueryKey = ['notifications', 'push', 'current-endpoint'] as const;

function permissionDeniedError(): ApiRequestError {
  return new ApiRequestError({
    statusCode: 403,
    code: 'PUSH_PERMISSION_DENIED',
    message: 'Push-Berechtigung wurde nicht erteilt.',
    timestamp: new Date().toISOString(),
    path: '/notifications/push/subscribe',
  });
}

export function usePushSubscription(enabled = true) {
  const supported = isPushSupported();
  const capable = usePushSubscriptionCapable(enabled && supported);

  if (!supported) {
    return buildUnsupportedPushSubscriptionSnapshot();
  }

  return capable;
}

function usePushSubscriptionCapable(enabled: boolean) {
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<PushPermissionState>(() =>
    readNotificationPermission(),
  );
  const [isAskingPermission, setIsAskingPermission] = useState(false);

  const vapid = useQuery({
    queryKey: pushVapidQueryKey,
    queryFn: fetchPushVapidPublicKey,
    enabled,
    staleTime: 5 * 60_000,
  });

  const serverEnabled = Boolean(vapid.data?.enabled);
  const devicesQueryEnabled = enabled && serverEnabled;

  const devices = useQuery({
    queryKey: pushDevicesQueryKey,
    queryFn: fetchPushDevices,
    enabled: devicesQueryEnabled,
  });

  const currentEndpointQuery = useQuery({
    queryKey: pushCurrentEndpointQueryKey,
    queryFn: readCurrentPushEndpoint,
    enabled,
  });
  const currentEndpoint = currentEndpointQuery.data ?? null;

  const refreshCurrentEndpoint = useCallback(async () => {
    await queryClient.fetchQuery({
      queryKey: pushCurrentEndpointQueryKey,
      queryFn: readCurrentPushEndpoint,
    });
    setPermission(readNotificationPermission());
  }, [queryClient]);

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      if (Notification.permission !== 'granted') {
        throw permissionDeniedError();
      }

      const result = await subscribeAndSyncPushSubscription({
        vapidPublicKey: vapid.data?.publicKey,
        createIfMissing: true,
      });
      if (!result) {
        throw permissionDeniedError();
      }
      queryClient.setQueryData(pushCurrentEndpointQueryKey, result.endpoint);
    },
    onSuccess: () => {
      clearPushPromptDeclinedOnDevice();
      void queryClient.invalidateQueries({ queryKey: pushDevicesQueryKey });
    },
  });

  /**
   * Pass a promise from `Notification.requestPermission()` started in the click handler
   * (before any await). VAPID fetch and subscribe run only after permission is granted.
   */
  async function enablePush(permissionPromise: Promise<NotificationPermission>) {
    setIsAskingPermission(true);
    try {
      const nextPermission = await permissionPromise;
      setPermission(nextPermission);
      if (nextPermission !== 'granted') {
        throw permissionDeniedError();
      }
      await subscribeMutation.mutateAsync();
    } finally {
      setIsAskingPermission(false);
    }
  }

  const removeDeviceMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      const device = devices.data?.items.find((entry) => entry.id === deviceId);
      await deletePushDevice(deviceId);

      const localEndpoint =
        queryClient.getQueryData<string | null>(pushCurrentEndpointQueryKey) ?? null;
      if (device?.endpoint === localEndpoint) {
        await unregisterCurrentPushDevice(device.endpoint);
        queryClient.setQueryData(pushCurrentEndpointQueryKey, null);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: pushDevicesQueryKey });
    },
  });

  const deviceItems = devices.data?.items ?? [];
  const hasLocalPushSubscription = currentEndpoint != null;
  const isActiveOnThisDevice =
    hasLocalPushSubscription && deviceItems.some((device) => device.endpoint === currentEndpoint);

  return {
    supported: true as const,
    serverEnabled,
    permission,
    isActiveOnThisDevice,
    currentEndpoint,
    devices: deviceItems,
    isLoading:
      (enabled && currentEndpointQuery.isLoading) ||
      (enabled && vapid.isLoading) ||
      (devicesQueryEnabled && devices.isLoading),
    isEnabling: isAskingPermission || subscribeMutation.isPending,
    isRemoving: removeDeviceMutation.isPending,
    enablePush,
    removeDevice: removeDeviceMutation.mutateAsync,
    refreshCurrentEndpoint,
    enableError: subscribeMutation.error,
    removeError: removeDeviceMutation.error,
  };
}
